// src/components/EvaluationConfigurationStepTwo.js
// Paso 2 del wizard: competencias + caso de estudio por competencia.
// Desde PLAN_CASOS_ESTUDIO.md (2026-09-02): el caso se elige entre los casos
// vinculados a la competencia (capability.casos[] → catálogo caseStudies); varias
// competencias pueden apuntar al mismo caso y el candidato lo lee una sola vez.
// El tiempo de lectura es por caso (bloque "Casos de estudio del assessment"),
// el tiempo de respuesta sigue por competencia.
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Quiz as QuizIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Warning as WarningIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const EvaluationConfigurationStepTwo = ({ selectedPosition, competencies, onCompetenciesChange }) => {
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localCompetencies, setLocalCompetencies] = useState(competencies?.capabilities || []);
  // Catálogo de casos de estudio (id → caso) para resolver título/contenido/lectura
  const [caseCatalog, setCaseCatalog] = useState({});
  // Tiempo de lectura por caso, ajustable por assessment (caseStudyId → minutos)
  const [caseReadingTimes, setCaseReadingTimes] = useState(competencies?.caseReadingTimes || {});
  // Accordions de competencias: ids expandidos. Al montar se expanden todas;
  // una competencia nueva se agrega expandida y las demás se colapsan.
  const [expandedIds, setExpandedIds] = useState(
    () => new Set((competencies?.capabilities || []).map((c) => c.id))
  );
  const isExpanded = (id) => expandedIds.has(id);
  const toggleExpanded = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setExpandedIds(new Set(localCompetencies.map((c) => c.id)));
  const collapseAll = () => setExpandedIds(new Set());


  const { authToken, showAlert, language, organization } = useStore.getState();
  const { t } = useTranslation();

  const selectedCompetencies = competencies?.capabilities || [];

  const hasValidQuestions = (preguntas) => (preguntas || []).some((pregunta) => {
    const questionText = typeof pregunta === 'string' ? pregunta : pregunta.pregunta || '';
    return questionText.trim() !== '';
  });

  // Casos disponibles para una competencia, normalizados a
  // { id, titulo, contenido, preguntas, readingTime, legacy }.
  // 1) casos[] vinculados al catálogo (activos, con contenido y preguntas)
  // 2) textos[] antiguos si la competencia aún no fue migrada (cada uno es su
  //    propio caso, como hoy) — se marcan legacy.
  const getValidCapabilityTexts = (capability, catalog = caseCatalog) => {
    const linked = (capability.casos || [])
      .map((caso) => {
        const cs = catalog[caso.caseStudyId];
        if (!cs || cs.isActive === false) return null;
        if (!(cs.title || '').trim() || !(cs.content || '').trim()) return null;
        if (!hasValidQuestions(caso.preguntas)) return null;
        return {
          id: caso.caseStudyId,
          titulo: cs.title,
          contenido: cs.content,
          preguntas: caso.preguntas,
          readingTime: Number(cs.readingTime) || 5,
          legacy: false,
        };
      })
      .filter(Boolean);

    if (linked.length > 0) return linked;

    return (capability.textos || [])
      .filter((texto) => (texto.titulo || '').trim() !== '' && (texto.contenido || '').trim() !== '' && hasValidQuestions(texto.preguntas))
      .map((texto) => ({
        id: texto.id,
        titulo: texto.titulo,
        contenido: texto.contenido,
        preguntas: texto.preguntas,
        readingTime: 5,
        legacy: true,
      }));
  };

  // Agrupa las competencias por caso (orden de primera aparición) y arma el
  // snapshot caseStudies[] que se persiste en la evaluación.
  const buildCaseStudies = (comps, readingTimes = caseReadingTimes) => {
    const groups = [];
    const byId = {};
    comps.forEach((comp) => {
      if (!comp.selectedText) return;
      if (!byId[comp.selectedText]) {
        byId[comp.selectedText] = {
          caseStudyId: comp.selectedText,
          title: comp.selectedTextTitle || '',
          content: comp.selectedTextContent || '',
          readingTime: Number(readingTimes[comp.selectedText]) || Number(comp.selectedCaseReadingTime) || 5,
          order: groups.length + 1,
          competencies: [],
        };
        groups.push(byId[comp.selectedText]);
      }
      byId[comp.selectedText].competencies.push(comp);
    });
    return groups;
  };

  const hasDuplicateCapabilities = (comps) => {
    const ids = comps.map((c) => c.capability).filter(Boolean);
    return new Set(ids).size !== ids.length;
  };

  useEffect(() => {
    setLocalCompetencies(competencies?.capabilities || []);
  }, [competencies]);

  const emitToParent = (nextCapabilities, readingTimes = caseReadingTimes) => {
    const allCompetenciesValid = nextCapabilities.every(comp =>
      comp.capability && comp.selectedText &&
      comp.questions && comp.questions.length >= 3 && comp.questions.length <= 5
    );

    const caseStudies = buildCaseStudies(nextCapabilities, readingTimes);

    // Number(): los TextField entregan strings; sin coerción la suma
    // concatena ("35" + "15" = "3515") y corrompe totalTime/timeLimit.
    // Lectura: una vez por caso. Respuesta: por competencia.
    const totalTime =
      caseStudies.reduce((total, cs) => total + (Number(cs.readingTime) || 5), 0) +
      nextCapabilities.reduce((total, comp) => total + (Number(comp.questionTime) || 10), 0);

    const nextTotalPercentage = nextCapabilities.reduce((sum, comp) => {
      return sum + (parseFloat(comp.percentage) || 0);
    }, 0);

    const isValid = nextTotalPercentage === 100 && nextCapabilities.length > 0 &&
      allCompetenciesValid && !hasDuplicateCapabilities(nextCapabilities);

    onCompetenciesChangeRef.current({
      capabilities: nextCapabilities,
      caseStudies: caseStudies.map(({ competencies: _c, ...cs }) => cs),
      caseReadingTimes: readingTimes,
      isValid,
      totalPercentage: nextTotalPercentage,
      totalTime
    });
  };


  // Cargar capabilities al montar el componente
  useEffect(() => {
    loadCapabilities();
  }, []);

  const loadCapabilities = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = { Authorization: `Bearer ${authToken}` };
      const params = { language: language, organizationId: organization };
      const [response, catalogResponse] = await Promise.all([
        api.get(`/capabilities`, { params, headers }),
        // includeInactive: un caso inactivo no se ofrece, pero se resuelve su título
        api.get(`/case-studies`, { params: { ...params, includeInactive: true }, headers }),
      ]);

      const catalog = {};
      if (catalogResponse.data.response && catalogResponse.data.response.status_response === 'OK') {
        (catalogResponse.data.response.data || []).forEach((cs) => { catalog[cs.id || cs.uid] = cs; });
      }
      setCaseCatalog(catalog);

      if (response.data.response && response.data.response.status_response === 'OK') {
        const loadedCapabilities = response.data.response.data || [];
        // Solo competencias con al menos un caso usable (vinculado o legacy)
        const capabilitiesWithTexts = loadedCapabilities
          .map((capability) => ({
            ...capability,
            availableCases: getValidCapabilityTexts(capability, catalog),
          }))
          .filter((capability) => capability.availableCases.length > 0);

        setCapabilities(capabilitiesWithTexts);

        // Competencias predeterminadas del cargo (2026-09-09): si el paso parte
        // vacío y el cargo tiene predeterminadas, se precarga una fila por cada
        // una (solo las que tengan casos disponibles), con la competencia ya
        // elegida y el resto (caso, preguntas, %) por completar — igual que si
        // el reclutador la hubiera agregado a mano. Si ya hay competencias
        // configuradas (volver desde el paso 3, editar) no se toca nada.
        const defaults = Array.isArray(selectedPosition?.competencias)
          ? selectedPosition.competencias
          : [];
        if ((competencies?.capabilities || []).length === 0 && defaults.length > 0) {
          const seen = new Set();
          const prefilled = defaults
            .map((c) => {
              const id = c.competencyId ?? c.id ?? c.uid;
              if (!id || seen.has(id)) return null;
              seen.add(id);
              const cap = capabilitiesWithTexts.find((k) => k.id === id || k.uid === id);
              if (!cap) return null;
              return {
                id: crypto?.randomUUID?.() || `temp-${Date.now()}-${id}`,
                capability: cap.id || cap.uid,
                capabilityName: cap.name,
                selectedText: '',
                selectedTextTitle: '',
                selectedTextContent: '',
                questions: [],
                percentage: '',
                selectedCaseReadingTime: 5,
                questionTime: 10,
                availableTexts: cap.availableCases,
                availableQuestions: [],
                isNew: true,
              };
            })
            .filter(Boolean);

          if (prefilled.length > 0) {
            setLocalCompetencies(prefilled);
            setExpandedIds(new Set(prefilled.map((p) => p.id)));
          }
        }
      } else {
        throw new Error(t('capabilities.loadError'));
      }
    } catch (err) {
      console.error('Error loading capabilities:', err);
      setError(t('capabilities.loadError'));
      showAlert(t('capabilities.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const totalPercentage = selectedCompetencies.reduce((sum, comp) => {
    return sum + (parseFloat(comp.percentage) || 0);
  }, 0);


  const onCompetenciesChangeRef = useRef(onCompetenciesChange);

  // Notificar cambios al componente padre
  useEffect(() => {
    const allCompetenciesValid = selectedCompetencies.every(comp =>
      comp.capability && comp.selectedText &&
      comp.questions && comp.questions.length >= 3 && comp.questions.length <= 5
    );

    const caseStudies = buildCaseStudies(selectedCompetencies);

    // Number(): ver comentario en emitToParent (evita concatenación de strings)
    const totalTime =
      caseStudies.reduce((total, cs) => total + (Number(cs.readingTime) || 5), 0) +
      selectedCompetencies.reduce((total, comp) => total + (Number(comp.questionTime) || 10), 0);

    const isValid = totalPercentage === 100 && selectedCompetencies.length > 0 &&
      allCompetenciesValid && !hasDuplicateCapabilities(selectedCompetencies);

    onCompetenciesChangeRef.current({
      capabilities: selectedCompetencies,
      caseStudies: caseStudies.map(({ competencies: _c, ...cs }) => cs),
      caseReadingTimes,
      isValid,
      totalPercentage,
      totalTime
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompetencies, totalPercentage]);

  // Función para agregar competencia
  const handleAddCompetency = () => {
    const newCompetency = {
      id: crypto?.randomUUID?.() || `temp-${Date.now()}`, // clave estable
      capability: '',
      capabilityName: '',
      selectedText: '',
      selectedTextTitle: '',
      selectedTextContent: '',
      questions: [],
      percentage: '',
      selectedCaseReadingTime: 5,
      questionTime: 10,
      availableTexts: [],
      availableQuestions: [],
      isNew: true
    };
    const next = [...selectedCompetencies, newCompetency];
    setExpandedIds(new Set([newCompetency.id]));
    emitToParent(next);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      emitToParent(localCompetencies, caseReadingTimes);
    }, 300); // espera 300ms después del último cambio
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localCompetencies, caseReadingTimes]);

  // Tiempo de lectura por caso (ajustable por assessment; default = catálogo)
  const handleCaseReadingTimeChange = (caseStudyId, value) => {
    setCaseReadingTimes(prev => ({ ...prev, [caseStudyId]: value }));
  };

  const handleCaseReadingTimeBlur = (caseStudyId, value) => {
    let validatedValue = parseInt(value) || 0;
    if (validatedValue < 5) validatedValue = 5;
    else if (validatedValue > 60) validatedValue = 60;
    setCaseReadingTimes(prev => ({ ...prev, [caseStudyId]: validatedValue }));
  };

  // Función para eliminar competencia
  const handleRemoveCompetency = (index) => {
    setLocalCompetencies(prev => prev.filter((_, i) => i !== index));
  };

  // Función para actualizar competencia
  const handleUpdateCompetency = (index, field, value) => {
    setLocalCompetencies(prev =>
      prev.map((comp, i) => (i === index ? { ...comp, [field]: value } : comp))
    );
  };

  const handleTimingBlur = (index, field, value) => {
    let validatedValue = parseInt(value) || 0;
    if (validatedValue < 5) validatedValue = 5;
    else if (validatedValue > 60) validatedValue = 60;

    setLocalCompetencies(prev =>
      prev.map((comp, i) =>
        i === index ? { ...comp, [field]: validatedValue } : comp
      )
    );
  };

  // Función para actualizar cuando cambia la competencia
  const handleCapabilityChange = (index, capabilityId) => {
    const selectedCapability = capabilities.find(
      cap => cap.id === capabilityId || cap.uid === capabilityId
    );
    if (!selectedCapability) return;

    setLocalCompetencies(prev =>
      prev.map((comp, i) =>
        i === index
          ? {
            ...comp,
            capability: capabilityId,
            capabilityName: selectedCapability.name,
            selectedText: '',
            selectedTextTitle: '',
            selectedTextContent: '',
            questions: [],
            availableTexts: selectedCapability.availableCases || getValidCapabilityTexts(selectedCapability),
            availableQuestions: [],
          }
          : comp
      )
    );
  };

  // Función para actualizar cuando cambia el texto seleccionado
  const handleTextChange = (index, textId) => {
    setLocalCompetencies(prev =>
      prev.map((comp, i) => {
        if (i !== index) return comp;
        const selectedText = comp.availableTexts.find(texto => texto.id === textId);
        if (!selectedText) return comp;
        return {
          ...comp,
          selectedText: textId,
          selectedTextTitle: selectedText.titulo || t('evaluations.noTitle'),
          selectedTextContent: selectedText.contenido || '',
          selectedCaseReadingTime: Number(selectedText.readingTime) || 5,
          selectedCaseLegacy: !!selectedText.legacy,
          questions: [],
          availableQuestions: selectedText.preguntas || [],
        };
      })
    );
    // Default del tiempo de lectura del caso (solo si no fue ajustado ya)
    setCaseReadingTimes(prev => {
      if (prev[textId] !== undefined) return prev;
      const comp = localCompetencies[index];
      const selectedText = comp?.availableTexts?.find(texto => texto.id === textId);
      return { ...prev, [textId]: Number(selectedText?.readingTime) || 5 };
    });
  };

  // Función para manejar selección múltiple de preguntas
  const handleQuestionsChange = (index, selectedQuestions) => {
    if (selectedQuestions.length > 5) {
      showAlert(t('evaluations.maxQuestionsPerCompetency'), 'warning');
      return;
    }

    setLocalCompetencies(prev =>
      prev.map((comp, i) =>
        i === index ? { ...comp, questions: selectedQuestions } : comp
      )
    );
  };

  // Función para verificar si una pregunta está seleccionada
  const isQuestionSelected = (competencyIndex, questionText) => {
    const competency = localCompetencies?.[competencyIndex];
    return competency?.questions?.includes(questionText) || false;
  };

  // Función para toggle pregunta individual
  const toggleQuestion = (competencyIndex, questionText) => {
    setLocalCompetencies(prev => {
      const competency = prev[competencyIndex];
      if (!competency) return prev;

      const currentQuestions = competency.questions || [];
      let newQuestions;

      if (currentQuestions.includes(questionText)) {
        if (currentQuestions.length > 3) {
          newQuestions = currentQuestions.filter(q => q !== questionText);
        } else {
          showAlert(t('evaluations.minQuestionsPerCompetency'), 'warning');
          return prev;
        }
      } else {
        if (currentQuestions.length < 5) {
          newQuestions = [...currentQuestions, questionText];
        } else {
          showAlert(t('evaluations.maxQuestionsPerCompetency'), 'warning');
          return prev;
        }
      }

      return prev.map((comp, i) =>
        i === competencyIndex ? { ...comp, questions: newQuestions } : comp
      );
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadCapabilities}>
            <RefreshIcon sx={{ mr: 1 }} />
            {t('retry')}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const validCompetencies = localCompetencies.filter(
    (comp) => !comp.capability || capabilities.some(
      (cap) => cap.id === comp.capability || cap.uid === comp.capability
    )
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
        {t('evaluations.stepTwoTitle')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('evaluations.stepTwoSubTitle')}
      </Typography>

      {/* Información del cargo seleccionado */}
      {selectedPosition && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            <strong>{t('reports.layout.selectedPosition')}:</strong>
          </Typography>
          <Typography variant="body2">
            {selectedPosition.name}
          </Typography>
        </Paper>
      )}

      {/* Alerta informativa sobre las preguntas */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<QuizIcon />}>
        <Typography variant="subtitle2" gutterBottom>
          {t('evaluations.questionsConfigurationTitle')}
        </Typography>
        <Typography variant="body2">
          {t('evaluations.questionsConfigurationDescription')}
        </Typography>
      </Alert>

      {/* Competencias (accordions colapsables) */}
      {validCompetencies.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
          <Button size="small" onClick={expandAll}>{t('evaluations.expandAll')}</Button>
          <Button size="small" onClick={collapseAll}>{t('evaluations.collapseAll')}</Button>
        </Box>
      )}
      <Box sx={{ mb: 3 }}>
        {validCompetencies.map((competency, index) => {
          const questionCount = competency.questions?.length || 0;
          const isDuplicated = localCompetencies.some((c, ci) => ci !== index && c.capability && c.capability === competency.capability);
          const isComplete = competency.capability && competency.selectedText && questionCount >= 3 && questionCount <= 5 &&
            parseFloat(competency.percentage) > 0 && !isDuplicated;
          return (
          <Accordion
            key={competency.id}
            expanded={isExpanded(competency.id)}
            onChange={() => toggleExpanded(competency.id)}
            sx={{ mb: 2, border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: isExpanded(competency.id) ? 'transparent' : '#fafafa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'medium' }}>
                  <AssignmentIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('capability')} #{index + 1}
                  {competency.capabilityName ? `: ${competency.capabilityName}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 'auto', alignItems: 'center' }}>
                  <Chip
                    label={`${competency.percentage || 0}%`}
                    color={parseFloat(competency.percentage) > 0 ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip label={`${Number(competency.questionTime) || 10} min`} color="info" size="small" />
                  {competency.selectedTextTitle && (
                    <Chip label={competency.selectedTextTitle} color="info" variant="outlined" size="small" sx={{ maxWidth: 220 }} />
                  )}
                  {questionCount >= 3 && questionCount <= 5 && (
                    <Chip label={t('evaluations.questionsCount', { count: questionCount })} color="success" size="small" />
                  )}
                  {questionCount > 0 && questionCount < 3 && (
                    <Chip label={t('evaluations.questionsCountMinimum', { count: questionCount, minimum: 3 })} color="error" size="small" />
                  )}
                  {isDuplicated && (
                    <Chip label={t('evaluations.duplicatedCapability')} color="error" size="small" />
                  )}
                  {!isComplete && !isDuplicated && (
                    <Chip label={t('evaluations.configurationIncomplete')} color="warning" variant="outlined" size="small" />
                  )}
                  <IconButton
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleRemoveCompetency(index); }}
                    onFocus={(e) => e.stopPropagation()}
                    size="small"
                    title={t('delete')}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Selección de Competencia */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('evaluations.selectCapability')}</InputLabel>
                    <Select
                      value={competency.capability}
                      onChange={(e) => handleCapabilityChange(index, e.target.value)}
                      label={t('evaluations.selectCapability')}
                    >
                      {capabilities.map((capability) => (
                        <MenuItem key={capability.id || capability.uid} value={capability.id || capability.uid}>
                          {capability.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Peso/Porcentaje */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('evaluations.evaluationWeigth')}
                    type="number"
                    value={competency.percentage}
                    onChange={(e) => handleUpdateCompetency(index, 'percentage', e.target.value)}
                    inputProps={{ min: 0, max: 100 }}
                    error={parseFloat(competency.percentage) > 100}
                    helperText={parseFloat(competency.percentage) > 100 ? t('evaluations.cannotExceed100') : ''}
                  />
                </Grid>

                {/* Tiempo de respuesta por competencia (el de lectura va por caso, más abajo) */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('evaluations.questionTime')}
                    type="number"
                    value={competency.questionTime === '' ? '' : (competency.questionTime === undefined ? 10 : competency.questionTime)}
                    onChange={(e) => handleUpdateCompetency(index, 'questionTime', e.target.value)}
                    onBlur={(e) => handleTimingBlur(index, 'questionTime', e.target.value)}
                    inputProps={{ min: 5, max: 60 }}
                    helperText={t('evaluations.timeRangeHelper')}
                    error={competency.questionTime && (competency.questionTime < 5 || competency.questionTime > 60)}
                  />
                </Grid>

                {/* Selección de Texto/Caso */}
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!competency.capability}>
                    <InputLabel>{t('evaluations.selectCaseStudy')}</InputLabel>
                    <Select
                      value={competency.selectedText}
                      onChange={(e) => handleTextChange(index, e.target.value)}
                      label={t('evaluations.selectCaseStudy')}
                      renderValue={(selected) => {
                        const texto = competency.availableTexts.find(t => t.id === selected);
                        return texto ? texto.titulo || t('evaluations.noTitle') : '';
                      }}
                    >
                      {competency.availableTexts.map((texto) => (
                        <MenuItem key={texto.id} value={texto.id}>
                          <Box sx={{ py: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                              {texto.titulo || t('evaluations.noTitle')}
                              {texto.legacy && (
                                <Chip size="small" label={t('evaluations.legacyCase')} sx={{ ml: 1 }} />
                              )}
                              {!texto.legacy && localCompetencies.some((c, ci) => ci !== index && c.selectedText === texto.id) && (
                                <Chip size="small" color="info" label={t('evaluations.caseAlreadyInAssessment')} sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {texto.contenido ? `${texto.contenido.substring(0, 100)}...` : t('evaluations.noContent')}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Vista previa del caso de estudio */}
                  {competency.selectedTextContent && (
                    <Box sx={{ mt: 2 }}>
                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ backgroundColor: '#f8f9fa' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <VisibilityIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" color="primary">
                              {t('preview')}: {competency.selectedTextTitle}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="body2" sx={{
                              lineHeight: 1.6,
                              maxHeight: 300,
                              overflow: 'auto'
                            }}>
                              {competency.selectedTextContent}
                            </Typography>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Grid>

                {/* Selección Múltiple de Preguntas */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <QuizIcon sx={{ mr: 1, color: 'primary.main' }} />
                      {t('evaluations.selectQuestion')}
                      <Chip
                        size="small"
                        label={`${competency.questions?.length || 0}/5`}
                        color={
                          !competency.questions?.length ? 'default' :
                            competency.questions.length < 3 ? 'error' :
                              competency.questions.length >= 3 && competency.questions.length <= 5 ? 'success' : 'warning'
                        }
                        sx={{ ml: 1 }}
                      />
                    </Typography>

                    {competency.questions?.length < 3 && (
                      <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                        <Typography variant="body2">
                          {t('evaluations.selectMinimumQuestions', {
                            minimum: 3,
                            current: competency.questions?.length || 0
                          })}
                        </Typography>
                      </Alert>
                    )}
                  </Box>

                  <FormControl fullWidth disabled={!competency.selectedText}>
                    <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0' }}>
                      {competency.availableQuestions.length > 0 ? (
                        competency.availableQuestions.map((pregunta, qIndex) => (
                          <FormControlLabel
                            key={qIndex}
                            control={
                              <Checkbox
                                checked={isQuestionSelected(index, pregunta.pregunta)}
                                onChange={() => toggleQuestion(index, pregunta.pregunta)}
                                disabled={
                                  !isQuestionSelected(index, pregunta.pregunta) &&
                                  competency.questions?.length >= 5
                                }
                              />
                            }
                            label={
                              <Box sx={{ py: 1 }}>
                                <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                  <strong>{t('evaluations.questionNumber', { number: qIndex + 1 })}:</strong> {pregunta.pregunta}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              mb: 1,
                              p: 1,
                              borderRadius: 1,
                              backgroundColor: isQuestionSelected(index, pregunta.pregunta) ? '#e3f2fd' : 'transparent',
                              '&:hover': {
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                          {t('evaluations.noQuestionsAvailable')}
                        </Typography>
                      )}
                    </Paper>
                  </FormControl>

                  {/* Mostrar las preguntas seleccionadas */}
                  {competency.questions && competency.questions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ backgroundColor: '#e8f5e8' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CheckBoxIcon sx={{ mr: 1, color: 'success.main' }} />
                            <Typography variant="subtitle2" color="success.main">
                              {t('evaluations.selectedQuestions', { count: competency.questions.length })}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ p: 1 }}>
                            {competency.questions.map((question, qIndex) => (
                              <Paper key={qIndex} sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                  <strong style={{ marginRight: '8px', color: '#1976d2' }}>
                                    {qIndex + 1}.
                                  </strong>
                                  {question}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          );
        })}
      </Box>

      {/* Botón para agregar competencia */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddCompetency}
        sx={{ mb: 3 }}
        disabled={capabilities.length === 0}
        size="large"
      >
        {t('evaluations.addNewCapability')}
      </Button>

      {/* Mensaje si no hay capabilities */}
      {capabilities.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('evaluations.noCapabilitiesAvailable')}
        </Alert>
      )}

      {/* Casos de estudio del assessment: agrupación derivada + tiempo de lectura por caso */}
      {buildCaseStudies(localCompetencies).length > 0 && (
        <Paper sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t('evaluations.assessmentCaseStudies')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('evaluations.assessmentCaseStudiesHelper')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {buildCaseStudies(localCompetencies).map((cs, csIndex) => (
            <Box key={cs.caseStudyId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="subtitle1">
                  {csIndex + 1}. {cs.title || t('evaluations.noTitle')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  {cs.competencies.map((comp, ci) => (
                    <Chip key={comp.id || ci} size="small" color="primary" variant="outlined"
                      label={comp.capabilityName || t('evaluations.noCapability')} />
                  ))}
                </Box>
              </Box>
              <TextField
                label={t('evaluations.readingTime')}
                type="number"
                size="small"
                value={caseReadingTimes[cs.caseStudyId] === '' ? '' : (caseReadingTimes[cs.caseStudyId] ?? cs.readingTime)}
                onChange={(e) => handleCaseReadingTimeChange(cs.caseStudyId, e.target.value)}
                onBlur={(e) => handleCaseReadingTimeBlur(cs.caseStudyId, e.target.value)}
                inputProps={{ min: 5, max: 60 }}
                helperText={t('evaluations.timeRangeHelper')}
                sx={{ width: 180 }}
              />
            </Box>
          ))}
        </Paper>
      )}

      {/* Resumen final */}
      {localCompetencies.length > 0 && (
        <Paper sx={{
          p: 3,
          backgroundColor: totalPercentage === 100 ? '#e8f5e8' : '#fff3cd',
          border: `2px solid ${totalPercentage === 100 ? '#4caf50' : '#ff9800'}`,
          borderRadius: 2
        }}>
          <Typography variant="h6" gutterBottom color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <QuizIcon sx={{ mr: 1 }} />
            {t('evaluations.finalConfigurationSummary')}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                <Typography variant="h4" color="primary">
                  {localCompetencies.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('evaluations.configuredCapabilities')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('evaluations.caseStudiesCount', { count: buildCaseStudies(localCompetencies).length })}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                <Typography variant="h4" color={totalPercentage === 100 ? 'success.main' : 'warning.main'}>
                  {totalPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('evaluations.totalWeigthsAsigned')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main">
                  {localCompetencies.reduce((total, comp) => total + (comp.questions?.length || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('evaluations.totalQuestions')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
                <Typography variant="h4" color={
                  totalPercentage === 100 &&
                    localCompetencies.every(comp => comp.questions?.length >= 3 && comp.questions?.length <= 5)
                    ? 'success.main' : 'error.main'
                }>
                  {totalPercentage === 100 &&
                    localCompetencies.every(comp => comp.questions?.length >= 3 && comp.questions?.length <= 5)
                    ? '✓' : '✗'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {totalPercentage === 100 &&
                    localCompetencies.every(comp => comp.questions?.length >= 3 && comp.questions?.length <= 5)
                    ? t('evaluations.configurationComplete') : t('evaluations.configurationIncomplete')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Validaciones y advertencias */}
          {totalPercentage !== 100 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ {t('evaluations.weigthWarning')}
              </Typography>
              {totalPercentage < 100 && (
                <Typography variant="body2">
                  {t('evaluations.left')} <strong>{(100 - totalPercentage).toFixed(1)}%</strong> {t('evaluations.forAssignment')}.
                </Typography>
              )}
              {totalPercentage > 100 && (
                <Typography variant="body2">
                  {t('evaluations.youHave')} <strong>{(totalPercentage - 100).toFixed(1)}%</strong> {t('evaluations.surplusAssigned')}.
                </Typography>
              )}
            </Alert>
          )}

          {hasDuplicateCapabilities(localCompetencies) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('evaluations.duplicatedCapabilityError')}
            </Alert>
          )}

          {localCompetencies.some(comp => comp.capability && !comp.selectedText) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('evaluations.caseStudyRequiredError')}
            </Alert>
          )}

          {/* Validación de preguntas */}
          {localCompetencies.some(comp => !comp.questions?.length || comp.questions.length < 3 || comp.questions.length > 5) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('evaluations.questionsConfigurationRequired')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {localCompetencies.map((comp, index) => {
                  const questionCount = comp.questions?.length || 0;
                  if (questionCount < 3 || questionCount > 5) {
                    return (
                      <li key={index}>
                        <Typography variant="body2">
                          <strong>{comp.capabilityName || t('evaluations.competencyNumber', { number: index + 1 })}:</strong>
                          {questionCount === 0 && ` ${t('evaluations.noQuestionsSelected')}`}
                          {questionCount > 0 && questionCount < 3 && ` ${t('evaluations.insufficientQuestions', { count: questionCount, minimum: 3 })}`}
                          {questionCount > 5 && ` ${t('evaluations.excessQuestions', { count: questionCount, maximum: 5 })}`}
                        </Typography>
                      </li>
                    );
                  }
                  return null;
                })}
              </Box>
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default EvaluationConfigurationStepTwo;